/**
 * Tasks and everything attached to them: comments, blockers, watchers and the
 * dependency links, plus the in-app notifications they produce.
 */

import { ifMatch, request } from './client.js';
import { pageCall, type RegisterPage } from './paging.js';
// ---------------------------------------------------------------- notifications

export interface Notification {
  readonly id: string;
  readonly event_type: string;
  readonly entity_type: string;
  readonly entity_id: string | null;
  readonly title: string;
  readonly body: string | null;
  readonly severity: 'info' | 'warning' | 'urgent';
  readonly deep_link: string | null;
  readonly read_at: string | null;
  readonly created_at: string;
}

export interface NotificationPage {
  readonly data: readonly Notification[];
  readonly unread: number;
}

export function listNotifications(includeRead = false): Promise<NotificationPage> {
  return request<NotificationPage>(
    `/v1/me/notifications${includeRead ? '?include_read=true' : ''}`,
  );
}

export function markNotificationRead(id: string): Promise<NotificationPage> {
  return request<NotificationPage>(
    `/v1/me/notifications/${encodeURIComponent(id)}/read`,
    { method: 'POST' },
  );
}

export function markAllNotificationsRead(): Promise<NotificationPage> {
  return request<NotificationPage>('/v1/me/notifications/read-all', { method: 'POST' });
}

// ------------------------------------------------------------------------ tasks

export type TaskStatus = 'todo' | 'in_progress' | 'blocked' | 'done' | 'cancelled';
export type TaskPriority = 'low' | 'normal' | 'high' | 'critical';

export type CauseType =
  | 'drawing'
  | 'approval'
  | 'material'
  | 'predecessor'
  | 'access'
  | 'client_decision'
  | 'other';

export interface Blocker {
  readonly id: string;
  readonly cause_type: CauseType;
  readonly description: string;
  readonly severity: 'low' | 'medium' | 'high' | 'critical';
  readonly is_system_generated: boolean;
  readonly opened_at: string;
  readonly opened_by_name: string | null;
  readonly days_open: number;
}

export interface Task {
  readonly id: string;
  /**
   * The concurrency token, opaque. Hand it back to `updateTask` so a save on
   * top of somebody else's is refused rather than silently overwriting it.
   */
  readonly record_version: string;
  readonly title: string;
  readonly description: string | null;
  readonly status: TaskStatus;
  readonly priority: TaskPriority;
  readonly due_date: string | null;
  readonly owner_id: string | null;
  readonly owner_name: string | null;
  readonly location_id: string | null;
  readonly location_path: string | null;
  readonly completed_at: string | null;
  readonly created_at: string;
  /**
   * Whether finishing it demands a photograph (the user's rule, 2026-08-20).
   * PER TASK: "fit the ceiling grid" is ticked, "chase the consultant" is not.
   */
  readonly needs_photo_evidence: boolean;
  /** What THIS caller may move it to. Empty means no buttons. */
  readonly allowed_next: readonly TaskStatus[];
  readonly overdue: boolean;
  /** Why it is stopped. Empty unless blocked — the two always travel together. */
  readonly blockers: readonly Blocker[];
  /** How many comments THIS caller can see — internal ones are not counted for
   *  an outside party, so the number cannot hint at a conversation they cannot
   *  read. */
  readonly comment_count: number;
  /** Whether THIS caller has asked to hear about the task. */
  readonly watching: boolean;
}

export type Visibility = 'internal' | 'shared';

export interface Comment {
  readonly id: string;
  readonly body: string;
  readonly visibility: Visibility;
  readonly author_id: string;
  readonly author_name: string | null;
  readonly created_at: string;
  readonly edited_at: string | null;
  readonly is_mine: boolean;
}

async function commentCall(path: string, init?: RequestInit): Promise<readonly Comment[]> {
  const body = await request<{ data: readonly Comment[] }>(path, init ?? {});
  return body.data;
}

const commentsPath = (projectId: string, taskId: string): string =>
  `/v1/projects/${encodeURIComponent(projectId)}/tasks/${encodeURIComponent(taskId)}/comments`;

export function listTaskComments(
  projectId: string,
  taskId: string,
): Promise<readonly Comment[]> {
  return commentCall(commentsPath(projectId, taskId));
}

export function addTaskComment(
  projectId: string,
  taskId: string,
  input: { body: string; visibility?: Visibility },
): Promise<readonly Comment[]> {
  return commentCall(commentsPath(projectId, taskId), {
    method: 'POST',
    body: JSON.stringify(input),
  });
}

export function editTaskComment(
  projectId: string,
  taskId: string,
  commentId: string,
  input: { body: string; visibility?: Visibility },
): Promise<readonly Comment[]> {
  return commentCall(`${commentsPath(projectId, taskId)}/${encodeURIComponent(commentId)}`, {
    method: 'PATCH',
    body: JSON.stringify(input),
  });
}

export function deleteTaskComment(
  projectId: string,
  taskId: string,
  commentId: string,
): Promise<readonly Comment[]> {
  return commentCall(`${commentsPath(projectId, taskId)}/${encodeURIComponent(commentId)}`, {
    method: 'DELETE',
  });
}

export interface TaskInput {
  readonly title?: string;
  readonly description?: string | null;
  readonly owner_id?: string | null;
  readonly priority?: TaskPriority;
  readonly due_date?: string | null;
  readonly location_id?: string | null;
  readonly needs_photo_evidence?: boolean;
  readonly status?: TaskStatus;
  /** Required when moving to `blocked` — the server refuses without it. */
  readonly blocker?: {
    readonly cause_type: CauseType;
    readonly description: string;
    readonly severity?: 'low' | 'medium' | 'high' | 'critical';
  };
}

export function resolveTaskBlocker(
  projectId: string,
  taskId: string,
  blockerId: string,
  note?: string,
): Promise<readonly Task[]> {
  return taskCall(
    `/v1/projects/${encodeURIComponent(projectId)}/tasks/${encodeURIComponent(taskId)}` +
      `/blockers/${encodeURIComponent(blockerId)}/resolve`,
    { method: 'POST', body: JSON.stringify(note ? { note } : {}) },
  );
}

async function taskCall(path: string, init?: RequestInit): Promise<readonly Task[]> {
  const body = await request<{ data: readonly Task[] }>(path, init ?? {});
  return body.data;
}

/**
 * The board, which the server bounds. Returns the rows AND how many match the
 * SAME filters — see `api/paging.ts`.
 *
 * The filters go to the server; the search box on the screen still works over
 * what is loaded, which is why the total has to come back with it.
 */
export function listTasks(
  projectId: string,
  filters: { status?: string; owner_id?: string } = {},
  limit?: number,
): Promise<RegisterPage<Task>> {
  const query = new URLSearchParams();
  if (filters.status) query.set('status', filters.status);
  if (filters.owner_id) query.set('owner_id', filters.owner_id);
  const suffix = query.toString() ? `?${query.toString()}` : '';
  return pageCall<Task>(`/v1/projects/${encodeURIComponent(projectId)}/tasks${suffix}`, limit);
}

export interface MyTask extends Task {
  readonly project_id: string;
  readonly project_code: string;
  readonly project_name: string;
}

/** Everything assigned to me, across every project. */
export async function listMyTasks(includeFinished = false): Promise<readonly MyTask[]> {
  const body = await request<{ data: readonly MyTask[] }>(
    `/v1/me/tasks${includeFinished ? '?include_finished=true' : ''}`,
  );
  return body.data;
}

export function createTask(projectId: string, input: TaskInput): Promise<readonly Task[]> {
  return taskCall(`/v1/projects/${encodeURIComponent(projectId)}/tasks`, {
    method: 'POST',
    body: JSON.stringify(input),
  });
}

export function updateTask(
  projectId: string,
  taskId: string,
  input: TaskInput,
  /** The `record_version` this edit was started from. See `ifMatch`. */
  expectedVersion?: string,
): Promise<readonly Task[]> {
  return taskCall(
    `/v1/projects/${encodeURIComponent(projectId)}/tasks/${encodeURIComponent(taskId)}`,
    { method: 'PATCH', body: JSON.stringify(input), ...ifMatch(expectedVersion) },
  );
}


// ---------------------------------------------------------------------------
// Watchers and dependency links (0029)
// ---------------------------------------------------------------------------

export function setTaskWatching(
  projectId: string,
  taskId: string,
  watching: boolean,
): Promise<readonly Task[]> {
  return taskCall(
    `/v1/projects/${encodeURIComponent(projectId)}/tasks/${encodeURIComponent(taskId)}/watch`,
    { method: 'PATCH', body: JSON.stringify({ watching }) },
  );
}

export interface DependencyLink {
  readonly id: string;
  readonly from_type: string;
  readonly from_id: string;
  readonly to_type: string;
  readonly to_id: string;
  readonly link_type: string;
  readonly lag_days: number;
  readonly from_label: string | null;
  readonly to_label: string | null;
}

export interface LinksView {
  readonly links: readonly DependencyLink[];
  /** The loop through the new link, named, when one exists — a warning, not a refusal. */
  readonly cycle: readonly { type: string; id: string; label: string | null }[] | null;
}

export async function listLinks(
  projectId: string,
  entityType: string,
  entityId: string,
): Promise<readonly DependencyLink[]> {
  const body = await request<{ data: readonly DependencyLink[] }>(
    `/v1/projects/${encodeURIComponent(projectId)}/links?entity_type=${encodeURIComponent(entityType)}&entity_id=${encodeURIComponent(entityId)}`,
  );
  return body.data;
}

export function createDependencyLink(
  projectId: string,
  input: {
    from_type: string;
    from_id: string;
    to_type: string;
    to_id: string;
    link_type: string;
    lag_days?: number;
  },
): Promise<LinksView> {
  return request<LinksView>(`/v1/projects/${encodeURIComponent(projectId)}/links`, {
    method: 'POST',
    body: JSON.stringify(input),
  });
}

export function deleteDependencyLink(projectId: string, linkId: string): Promise<{ ok: boolean }> {
  return request<{ ok: boolean }>(
    `/v1/projects/${encodeURIComponent(projectId)}/links/${encodeURIComponent(linkId)}`,
    { method: 'DELETE' },
  );
}
